import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  showCloseButton?: boolean;
  container?: HTMLElement | null;
  overflowVisible?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  panelClassName?: string;
  bodyClassName?: string;
}

const sizeToClass: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-md sm:max-w-md",
  md: "max-w-full sm:max-w-lg",
  lg: "max-w-full sm:max-w-xl lg:max-w-2xl",
  xl: "max-w-full sm:max-w-4xl lg:max-w-6xl",
};

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  showCloseButton = true,
  container,
  overflowVisible = false,
  closeOnBackdrop = true,
  closeOnEscape = true,
  panelClassName = "",
  bodyClassName = "",
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape) onClose();
    };
    window.addEventListener("keydown", handler);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handler);
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center sm:p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden
      />
      <div
        className={`relative w-full ${sizeToClass[size]} mx-0 sm:mx-4 bg-white rounded-none sm:rounded-xl shadow-xl border-0 sm:border border-gray-200 max-h-screen ${
          overflowVisible ? "overflow-visible" : "overflow-y-auto"
        } ${panelClassName}`}
        role="dialog"
        aria-modal="true">
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-gray-900 font-semibold text-sm sm:text-base pr-2 line-clamp-2">{title}</h3>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100 flex-shrink-0"
                aria-label="Kapat">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
        <div className={`p-4 sm:p-5 pb-6 sm:pb-5 ${bodyClassName}`}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, container || document.body);
}
